import React from "react";
import { motion } from "framer-motion";
import { formatNumber, formatFull, timeAgo } from "@/lib/format";
import {
  UsersThreeIcon, EyeIcon, FilmSlateIcon, CircleNotchIcon, CurrencyDollarIcon,
  PulseIcon, ArrowRightIcon, RocketLaunchIcon,
} from "@phosphor-icons/react";

const gradeColor = (g = "") => {
  const c = (g || "").toUpperCase();
  if (c === "A") return "#A3E635";
  if (c === "B") return "#FFFFFF";
  if (c === "C") return "#A0A0AB";
  return "#16C784";
};
const potentialColor = (s = 0) =>
  s >= 80 ? "#A3E635" : s >= 65 ? "#16C784" : s >= 50 ? "#FFFFFF" : "#A0A0AB";

const Bar = ({ pct, on }) => (
  <div className="h-1.5 bg-[#141417] overflow-hidden">
    <div className="h-full transition-all duration-700"
      style={{ width: `${Math.min(pct || 0, 100)}%`, background: on ? "#A3E635" : "#16C784" }} />
  </div>
);

const ChannelsOverview = ({ channels, loading, activeId, onSelect }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="overview-loading">
        <CircleNotchIcon size={34} className="text-[#16C784] spin-slow" />
      </div>
    );
  }

  return (
    <motion.div
      data-testid="channels-overview"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="flex items-center gap-3 mb-5">
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#A3E635]">// All Channels</div>
        <span className="font-mono text-xs text-[#5a5a63]">{channels.length} TRACKED</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {channels.map((c, i) => {
          const m = c.metrics;
          return (
            <motion.button
              key={c.channel_id}
              data-testid={`overview-card-${c.channel_id}`}
              onClick={() => onSelect(c.channel_id)}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className={`group text-left relative bg-[#0C0C0E] border p-4 transition-colors duration-200 ${
                c.channel_id === activeId ? "border-[#16C784]/70" : "border-[#222226] hover:border-[#16C784]/50"
              }`}
              style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05)" }}
            >
              {/* header */}
              <div className="flex items-center gap-3">
                {c.thumbnail && (
                  <img src={c.thumbnail} alt="" referrerPolicy="no-referrer"
                    className="w-12 h-12 rounded-full border border-[#222226] object-cover shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-rajdhani font-bold text-lg text-white truncate">{c.title}</div>
                  <div className="font-mono text-[10px] text-[#5a5a63] truncate">
                    {c.handle ? `${c.handle} · ` : ""}{c.last_synced ? `synced ${timeAgo(c.last_synced)}` : ""}
                  </div>
                </div>
                {c.grade && (
                  <div className="shrink-0 w-11 h-11 rounded-full border-2 flex flex-col items-center justify-center"
                    style={{ borderColor: gradeColor(c.grade) }}>
                    <span className="font-rajdhani font-bold text-xl leading-none" style={{ color: gradeColor(c.grade) }}>{c.grade}</span>
                  </div>
                )}
              </div>

              {/* top stats */}
              <div className="grid grid-cols-3 gap-2 mt-4">
                <div className="border border-[#222226] bg-[#050505] p-2">
                  <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-[#5a5a63]"><UsersThreeIcon size={11} /> Subs</div>
                  <div className="font-rajdhani font-bold text-lg text-white">{formatNumber(c.subscriber_count)}</div>
                </div>
                <div className="border border-[#222226] bg-[#050505] p-2">
                  <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-[#5a5a63]"><EyeIcon size={11} /> Views</div>
                  <div className="font-rajdhani font-bold text-lg text-white">{formatNumber(c.view_count)}</div>
                </div>
                <div className="border border-[#222226] bg-[#050505] p-2">
                  <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-[#5a5a63]"><FilmSlateIcon size={11} /> Videos</div>
                  <div className="font-rajdhani font-bold text-lg text-white">{formatNumber(c.video_count)}</div>
                </div>
              </div>

              {m ? (
                <>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div className="border border-[#222226] bg-[#050505] p-2">
                      <div className="text-[9px] uppercase tracking-wider text-[#5a5a63]">Avg Views / Post</div>
                      <div className="font-rajdhani font-bold text-base text-white" title={formatFull(m.avg_views_per_post)}>{formatNumber(m.avg_views_per_post)}</div>
                    </div>
                    <div className="border border-[#222226] bg-[#050505] p-2">
                      <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-[#5a5a63]"><PulseIcon size={11} /> Engagement</div>
                      <div className="font-rajdhani font-bold text-base text-white">{m.engagement_rate}%</div>
                    </div>
                  </div>

                  {m.potential_score != null && (
                    <div className="mt-2 flex items-center justify-between border border-[#222226] bg-[#050505] p-2" data-testid={`overview-potential-${c.channel_id}`}>
                      <div className="flex items-center gap-1.5 min-w-0">
                        <RocketLaunchIcon size={12} className="text-[#16C784] shrink-0" weight="fill" />
                        <span className="font-mono text-[9px] uppercase tracking-wider text-[#5a5a63]">Potential</span>
                        <span className="font-rajdhani font-bold text-sm" style={{ color: potentialColor(m.potential_score) }}>{m.potential_score}</span>
                        <span className="font-mono text-[10px] text-[#A0A0AB] truncate">· {m.potential_tier}</span>
                      </div>
                      <span className="font-mono text-[9px] text-[#5a5a63] shrink-0">{m.channel_age_label}</span>
                    </div>
                  )}

                  {/* monetization progress */}
                  <div className="mt-3 space-y-2">
                    <div>
                      <div className="flex items-center justify-between font-mono text-[10px] text-[#A0A0AB] mb-1">
                        <span>Subs → 1K</span>
                        <span style={{ color: m.eligible_subs ? "#A3E635" : "#A0A0AB" }}>{m.eligible_subs ? "Eligible ✓" : `${m.subs_progress_pct}%`}</span>
                      </div>
                      <Bar pct={m.subs_progress_pct} on={m.eligible_subs} />
                    </div>
                    <div>
                      <div className="flex items-center justify-between font-mono text-[10px] text-[#A0A0AB] mb-1">
                        <span>Watch hrs → 4K</span>
                        <span style={{ color: m.eligible_hours ? "#A3E635" : "#A0A0AB" }}>{m.eligible_hours ? "Eligible ✓" : `${m.hours_progress_pct}%`}</span>
                      </div>
                      <Bar pct={m.hours_progress_pct} on={m.eligible_hours} />
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#1a1a1e]">
                    <div className="flex items-center gap-1.5 text-[#A3E635]">
                      <CurrencyDollarIcon size={14} weight="fill" />
                      <span className="font-rajdhani font-bold text-sm">${formatNumber(m.est_monthly_revenue_low)}–${formatNumber(m.est_monthly_revenue_high)}/mo</span>
                    </div>
                    {m.eta_label && (
                      <div className="flex items-center gap-1 font-mono text-[10px] text-[#5a5a63]">
                        <RocketLaunchIcon size={11} className="text-[#16C784]" /> {m.eta_label}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="mt-3 font-mono text-[10px] text-[#5a5a63] border-t border-[#1a1a1e] pt-3">
                  Open this channel and run a scan to see full diagnostics.
                </div>
              )}

              <div className="mt-3 flex items-center gap-1.5 font-rajdhani font-bold uppercase tracking-wider text-[11px] text-[#16C784] opacity-70 group-hover:opacity-100 transition-opacity duration-200">
                Open dashboard <ArrowRightIcon size={13} weight="bold" />
              </div>
            </motion.button>
          );
        })}
      </div>
    </motion.div>
  );
};

export default ChannelsOverview;
